import { computed, ref, shallowRef, type Ref } from 'vue'

interface CrossPageSelectionOptions<Row, Key extends string | number> {
  getRowKey: (row: Row) => Key
}

export default function useCrossPageSelection<Row, Key extends string | number>(
  options: CrossPageSelectionOptions<Row, Key>,
) {
  const selectedRowKeys = ref<Key[]>([]) as Ref<Key[]>
  const selectedRowMap = shallowRef(new Map<Key, Row>())

  const selectedRows = computed(() =>
    selectedRowKeys.value
      .map((key) => selectedRowMap.value.get(key))
      .filter((item): item is Row => item !== undefined),
  )

  const updateSelection = (keys: Key[], currentPageSelectedRows: Row[]) => {
    const selectedKeySet = new Set(keys)
    const nextMap = new Map(selectedRowMap.value)

    Array.from(nextMap.keys()).forEach((key) => {
      if (!selectedKeySet.has(key)) {
        nextMap.delete(key)
      }
    })

    currentPageSelectedRows.forEach((row) => {
      nextMap.set(options.getRowKey(row), row)
    })

    selectedRowKeys.value = keys
    selectedRowMap.value = nextMap
  }

  const refreshCurrentPageRows = (currentPageRows: Row[]) => {
    if (!selectedRowKeys.value.length) return

    const selectedKeySet = new Set(selectedRowKeys.value)
    const nextMap = new Map(selectedRowMap.value)
    let changed = false

    currentPageRows.forEach((row) => {
      const key = options.getRowKey(row)
      if (selectedKeySet.has(key)) {
        nextMap.set(key, row)
        changed = true
      }
    })

    if (changed) {
      selectedRowMap.value = nextMap
    }
  }

  const clearSelection = () => {
    selectedRowKeys.value = []
    selectedRowMap.value = new Map()
  }

  return {
    selectedRowKeys,
    selectedRows,
    updateSelection,
    refreshCurrentPageRows,
    clearSelection,
  }
}
